import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Box } from '@mui/material';

// 頁面組件
import LoginPage from './pages/LoginPage';
import ResidentDashboard from './pages/ResidentDashboard';
import TenantDashboard from './pages/TenantDashboard';
import SecurityGuardDashboard from './pages/SecurityGuardDashboard';
import PropertyManagerDashboard from './pages/PropertyManagerDashboard';
import VisitorDashboard from './pages/VisitorDashboard';
import NotFoundPage from './pages/NotFoundPage';

// 布局組件
import MainLayout from './components/layouts/MainLayout';

// 特性組件
import WeatherWidget from './components/WeatherWidget';
import CCTVViewer from './components/CCTVViewer';
import UtilityMeters from './components/UtilityMeters';
import Notifications from './components/Notifications';
import PaymentUpload from './components/PaymentUpload';
import OTPGenerator from './components/OTPGenerator';
import SecurityAlerts from './components/SecurityAlerts';
import NFCPatrol from './components/NFCPatrol';
import ReportIssue from './components/ReportIssue';
import WorkOrder from './components/WorkOrder';
import VisitorAccess from './components/VisitorAccess';

function App() {
  const { isAuthenticated, userRole } = useSelector((state) => state.auth);
  const location = useLocation();

  // 根據用戶角色獲取適當的儀表板路徑
  const getDashboardPath = () => {
    switch (userRole) {
      case 'resident':
        return '/resident';
      case 'tenant':
        return '/tenant';
      case 'security':
        return '/security';
      case 'manager':
        return '/manager';
      case 'visitor':
        return '/visitor';
      default:
        return '/login';
    }
  };

  // 受保護的路由 - 只有認證用戶可以訪問
  const ProtectedRoute = ({ children }) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
    return children;
  };

  // 角色特定路由 - 只有特定角色的用戶可以訪問
  const RoleRoute = ({ allowedRoles, children }) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
    
    if (!allowedRoles.includes(userRole)) {
      return <Navigate to={getDashboardPath()} replace />;
    }
    
    return children;
  };

  return (
    <Box className="app-container">
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        
        {/* 受保護的路由 */}
        <Route path="/" element={
          <ProtectedRoute>
            <Navigate to={getDashboardPath()} replace />
          </ProtectedRoute>
        } />

        {/* 住戶儀表板 */}
        <Route path="/resident/*" element={
          <RoleRoute allowedRoles={['resident']}>
            <MainLayout>
              <ResidentDashboard />
            </MainLayout>
          </RoleRoute>
        } />

        {/* 租戶儀表板 */}
        <Route path="/tenant/*" element={
          <RoleRoute allowedRoles={['tenant']}>
            <MainLayout>
              <TenantDashboard />
            </MainLayout>
          </RoleRoute>
        } />

        {/* 保安員儀表板 */}
        <Route path="/security/*" element={
          <RoleRoute allowedRoles={['security']}>
            <MainLayout>
              <SecurityGuardDashboard />
            </MainLayout>
          </RoleRoute>
        } />

        {/* 物業管理員儀表板 */}
        <Route path="/manager/*" element={
          <RoleRoute allowedRoles={['manager']}>
            <MainLayout>
              <PropertyManagerDashboard />
            </MainLayout>
          </RoleRoute>
        } />

        {/* 訪客儀表板 */}
        <Route path="/visitor/*" element={
          <RoleRoute allowedRoles={['visitor']}>
            <MainLayout>
              <VisitorDashboard />
            </MainLayout>
          </RoleRoute>
        } />

        {/* 404 頁面 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Box>
  );
}

export default App; 